import { COOKIE_CONSENT_CHANGED_EVENT, getConsent } from './cookie-consent';
import { ROUTE_CHANGE_EVENT } from '../router';

declare global {
  interface Window {
    // gtag.js expects Arguments objects in dataLayer (official snippet).
    dataLayer?: Array<IArguments | Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
let loaded = false;
let lastTrackedPath: string | null = null;

function loadGoogleAnalytics(): void {
  if (loaded || !GA_ID) return;

  window.dataLayer = window.dataLayer ?? [];
  // Official gtag snippet pushes the Arguments object, not a rest-parameter array.
  // Pushing an Array can leave config commands unprocessed → no hits in Realtime.
  window.gtag = function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  loaded = true;
  trackPageView();
}

export function trackPageView(pathname = location.pathname): void {
  if (!loaded || !GA_ID || !window.gtag) return;
  if (pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;

  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: location.href,
    page_title: document.title,
  });
}

export function initAnalytics(): void {
  const consent = getConsent();
  if (consent?.analytics) {
    loadGoogleAnalytics();
  }
}

export function setupAnalyticsListener(): void {
  window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, () => {
    initAnalytics();
  });

  window.addEventListener(ROUTE_CHANGE_EVENT, ((event: CustomEvent<{ pathname: string }>) => {
    trackPageView(event.detail.pathname);
  }) as EventListener);
}
